import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

// Leitura pública direta (fetch): evita puxar axios + api.js (enorme) pro pacote inicial.
const API_BASE = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
const getJson = async (path) => {
  const res = await fetch(`${API_BASE}${path}`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
};

// O backend manda logo/favicon como caminho (/api/settings/asset/...) em vez de base64
// inline; resolve contra o host da API pra funcionar também fora do mesmo domínio.
const resolveAsset = (url, width) => {
  if (!url) return null;
  if (!url.startsWith('/')) return url;
  return `${process.env.REACT_APP_BACKEND_URL || ''}${url}${width ? `&w=${width}` : ''}`;
};

const DEFAULT_SETTINGS = {
  logoLight: null,
  logoDark: null,
  favicon: null,
  primaryColor: '#1f284f',
};

const DEFAULT_PRICING = {
  minimumWage: 0,
  minimumWageByYear: [],
  specialPercent: 0,
  highPercent: 0,
  lowPercent: 0,
  specialPeriods: [],
  highPeriods: [],
  lowPeriods: [],
  beneficiariosIncluidos: 0,
  extraGuestPercent: 0,
  childPaysFromAge: 0,
  minNightsHighBeach: 0,
  minGuestsBeach: 4,
  annuityMinPercent: 0,
  firstAnnuityMonthsAfterAdesao: 12,
  subsidioPercent: 0,
  welcomeBonusAmount: 400,
  monthlyCreditType: 'fixed',
  monthlyCreditValue: 50,
  maxHighNights: null,
  maxLowNights: null,
  maxSpecialNights: null,
  minAdvanceDaysLow: 15,
  minAdvanceDaysHighSpecial: 40,
  carTypes: [],
};

const SettingsContext = createContext({
  settings: DEFAULT_SETTINGS,
  pricing: DEFAULT_PRICING,
  pricingLoaded: false,
  refreshSettings: async () => {},
  refreshPricing: async () => {},
});

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [pricing, setPricing] = useState(DEFAULT_PRICING);
  const [pricingLoaded, setPricingLoaded] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      const data = await getJson('/settings/public');
      setSettings({
        logoLight: resolveAsset(data?.logoLight, 480),
        logoDark: resolveAsset(data?.logoDark, 480),
        favicon: resolveAsset(data?.favicon, 64),
        primaryColor: data?.primaryColor || DEFAULT_SETTINGS.primaryColor,
      });
    } catch (err) {
      console.warn('Failed to load public settings, using defaults', err);
      setSettings(DEFAULT_SETTINGS);
    }
  }, []);

  const fetchPricing = useCallback(async () => {
    try {
      const data = await getJson('/pricing/settings');
      setPricing({
        minimumWage: Number(data?.minimumWage) || 0,
        minimumWageByYear: Array.isArray(data?.minimumWageByYear) ? data.minimumWageByYear : [],
        specialPercent: Number(data?.specialPercent) || 0,
        highPercent: Number(data?.highPercent) || 0,
        lowPercent: Number(data?.lowPercent) || 0,
        specialPeriods: Array.isArray(data?.specialPeriods) ? data.specialPeriods : [],
        highPeriods: Array.isArray(data?.highPeriods) ? data.highPeriods : [],
        lowPeriods: Array.isArray(data?.lowPeriods) ? data.lowPeriods : [],
        beneficiariosIncluidos: Number(data?.beneficiariosIncluidos) || 0,
        extraGuestPercent: Number(data?.extraGuestPercent) || 0,
        childPaysFromAge: Number(data?.childPaysFromAge) || 0,
        minNightsHighBeach: Number(data?.minNightsHighBeach) || 0,
        minGuestsBeach: data?.minGuestsBeach === undefined || data?.minGuestsBeach === null ? 4 : Number(data.minGuestsBeach) || 0,
        annuityMinPercent: Number(data?.annuityMinPercent) || 0,
        firstAnnuityMonthsAfterAdesao: Number(data?.firstAnnuityMonthsAfterAdesao) || 12,
        subsidioPercent: Number(data?.subsidioPercent) || 0,
        welcomeBonusAmount: data?.welcomeBonusAmount !== undefined ? Number(data.welcomeBonusAmount) : 400,
        monthlyCreditType: data?.monthlyCreditType || 'fixed',
        monthlyCreditValue: Number(data?.monthlyCreditValue) || 50,
        maxHighNights: data?.maxHighNights ?? null,
        maxLowNights: data?.maxLowNights ?? null,
        maxSpecialNights: data?.maxSpecialNights ?? null,
        minAdvanceDaysLow: Number(data?.minAdvanceDaysLow) || 15,
        minAdvanceDaysHighSpecial: Number(data?.minAdvanceDaysHighSpecial) || 40,
        carTypes: Array.isArray(data?.carTypes) ? data.carTypes.map((c) => ({
          name: c.name,
          minPricePerDay: c.minPricePerDay ?? c.pricePerDay ?? 0,
          maxPricePerDay: c.maxPricePerDay ?? c.pricePerDay ?? 0,
        })) : [],
      });
    } catch (err) {
      setPricing(DEFAULT_PRICING);
    } finally {
      setPricingLoaded(true);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
    fetchPricing();
  }, [fetchSettings, fetchPricing]);

  // Sync primary color into a CSS variable so Tailwind utilities pick it up
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const color = settings.primaryColor || DEFAULT_SETTINGS.primaryColor;
    document.documentElement.style.setProperty('--color-primary', color);
  }, [settings.primaryColor]);

  // Injeta o favicon salvo em runtime (o <link rel="icon"> do index.html é resolvido
  // antes do React rodar, então trocamos o href aqui quando há favicon configurado).
  useEffect(() => {
    if (typeof document === 'undefined' || !settings.favicon) return;
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.setAttribute('href', settings.favicon);
  }, [settings.favicon]);

  return (
    <SettingsContext.Provider
      value={{
        settings,
        pricing,
        pricingLoaded,
        refreshSettings: fetchSettings,
        refreshPricing: fetchPricing,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => useContext(SettingsContext);

export default SettingsContext;
